import React from 'react';
import { Link } from 'react-router-dom';

/**
 * PYQSection - Showcases Previous Year Questions benefits
 * 
 * AI IMAGE PROMPT for pyq-feature.jpg:
 * "Educational illustration showing a stack of exam papers with years labeled (2023, 2022, 2021) 
 * transforming into a modern digital interface on a laptop screen. Student analyzing patterns 
 * and trends with highlighted important questions. Warm orange and amber color scheme, 
 * professional 3D rendering, soft shadows, academic atmosphere"
 */

const PYQSection: React.FC = () => {
  const benefits = [
    {
      icon: 'fas fa-history',
      title: 'Past Trends Analysis',
      description: 'Understand exam patterns from previous years',
      color: '#e67e22'
    },
    {
      icon: 'fas fa-chart-pie',
      title: 'Topic Frequency',
      description: 'Identify frequently asked topics and questions',
      color: '#3498db'
    },
    {
      icon: 'fas fa-brain',
      title: 'Real Exam Feel',
      description: 'Practice with actual questions from past exams',
      color: '#9b59b6'
    },
    {
      icon: 'fas fa-trophy',
      title: 'Higher Success Rate',
      description: 'Students practicing PYQs score 25% higher on average',
      color: '#27ae60'
    }
  ];

  const stats = [
    { label: 'Years Covered', value: '20+' },
    { label: 'Questions', value: '15K+' },
    { label: 'Topics', value: '800+' }
  ];

  return (
    <section className="pyq-section py-5" style={{ background: '#ffffff' }}>
      <div className="container">
        <div className="row align-items-center">
          {/* Left - Content */}
          <div className="col-lg-6 mb-4 mb-lg-0 order-2 order-lg-1" data-aos="fade-right">
            <div className="pe-lg-4">
              {/* Badge */}
              <span
                className="badge rounded-pill px-3 py-2 mb-3"
                style={{ background: '#e67e2220', color: '#e67e22', fontSize: '14px', fontWeight: 600 }}
              >
                <i className="fas fa-archive me-2"></i>Previous Year Questions
              </span>
              <h1 style={{fontSize: '1.25rem', margin: 0}}>JEE Mains Previous Year Questions</h1>
              {/* Heading */}
              <h2 className="fw-bold mb-3" style={{ fontSize: '2.5rem', lineHeight: '1.2' }}>
                Master <span style={{ color: '#e67e22' }}>Real Exam</span> Questions
              </h2>

              {/* Description */}
              <p className="text-muted mb-4" style={{ fontSize: '1.1rem' }}>
                Practice with authentic questions from previous JEE and NEET exams.
                Understand exam patterns, identify important topics, and boost your confidence
                by solving questions that actually appeared in past examinations.
              </p>

              {/* Stats Row */}
              <div className="row g-3 mb-4">
                {stats.map((stat, index) => (
                  <div key={index} className="col-4">
                    <div
                      className="text-center p-3 rounded-4"
                      style={{ background: 'linear-gradient(135deg, #e67e2215, #d3540015)' }}
                    >
                      <div className="h3 fw-bold mb-0" style={{ color: '#e67e22' }}>
                        {stat.value}
                      </div>
                      <small className="text-muted">{stat.label}</small>
                    </div>
                  </div>
                ))}
              </div>

              {/* Benefits Grid */}
              <div className="row g-3 mb-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="col-6">
                    <div className="d-flex align-items-start">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                        style={{ width: '40px', height: '40px', background: benefit.color + '20', color: benefit.color }}
                      >
                        <i className={benefit.icon}></i>
                      </div>
                      <div>
                        <h6 className="fw-bold mb-1 small">{benefit.title}</h6>
                        <small className="text-muted" style={{ fontSize: '0.85rem' }}>
                          {benefit.description}
                        </small>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* CTA Buttons */}
              <div className="d-flex gap-3">
                <Link
                  to="/pyq-quiz-generator"
                  className="btn btn-lg rounded-pill px-4"
                  style={{ background: 'linear-gradient(135deg, #e67e22, #d35400)', color: 'white', border: 'none' }}
                >
                  <i className="fas fa-play-circle me-2"></i>
                  Start PYQ Practice
                </Link>
                <Link
                  to="/pyq-history"
                  className="btn btn-lg btn-outline-secondary rounded-pill px-4"
                >
                  View History
                  <i className="fas fa-arrow-right ms-2"></i>
                </Link>
              </div>
            </div>
          </div>

          {/* Right - Image */}
          <div className="col-lg-6 mb-4 mb-lg-0 order-1 order-lg-2" data-aos="fade-left">
            <div className="position-relative">
              <img
                src="/img/pyq-feature.jpg"
                alt="Previous Year Questions"
                className="img-fluid rounded-4 shadow-lg"
                style={{ width: '100%', height: 'auto' }}
              />
              {/* Floating Badge */}
              <div
                className="position-absolute bottom-0 start-0 m-4 px-4 py-3 rounded-4 shadow-lg"
                style={{ background: 'white' }}
              >
                <div className="d-flex align-items-center">
                  <i className="fas fa-check-circle text-success fa-2x me-3"></i>
                  <div>
                    <div className="fw-bold">Verified Questions</div>
                    <small className="text-muted">Official exam papers</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PYQSection;
